import styled, { CSSProperties } from "styled-components";
import { IPaddingStyleProps, paddingMixin, spacingMixin } from "../../helpers";
import { TColor } from "../../types";

const getDirection = ({ direction, flexDirection, column }: FlexProps): CSSProperties["flexDirection"] => {
    if (column) {
        return "column";
    }

    return direction || flexDirection || "row";
};

export type FlexProps = IPaddingStyleProps & {
    alignItems?: CSSProperties["alignItems"];
    backgroundColor?: TColor;
    basis?: CSSProperties["flexBasis"];
    borderColor?: TColor;
    borderRadius?: CSSProperties["borderRadius"];
    borderStyle?: CSSProperties["borderStyle"];
    borderWidth?: CSSProperties["borderWidth"];
    boxShadow?: CSSProperties["boxShadow"];
    color?: TColor;
    column?: boolean;
    direction?: CSSProperties["flexDirection"];
    fill?: TColor;
    flexDirection?: CSSProperties["flexDirection"];
    flexWrap?: CSSProperties["flexWrap"];
    gap?: CSSProperties["gap"];
    grow?: CSSProperties["flexGrow"];
    shrink?: CSSProperties["flexShrink"];
    height?: CSSProperties["height"];
    maxHeight?: CSSProperties["maxHeight"];
    minHeight?: CSSProperties["minHeight"];
    justifyContent?: CSSProperties["justifyContent"];
    spacing?: number;
    width?: CSSProperties["width"];
    maxWidth?: CSSProperties["maxWidth"];
    minWidth?: CSSProperties["minWidth"];
};

export const Flex = styled.div.withConfig({
    shouldForwardProp: (prop, validAttr) =>
        validAttr(prop) && !["color", "column", "direction", "fill", "height", "spacing", "width"].includes(prop)
})<FlexProps>`
    display: flex;
    flex-direction: ${getDirection};

    ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
    ${({ basis }) => basis !== undefined && `flex-basis: ${basis};`}
	${({ backgroundColor, theme }) => backgroundColor && `background-color: ${theme.v2.colors[backgroundColor]};`}
	${({ borderColor, theme }) => borderColor && `border-color: ${theme.v2.colors[borderColor]};`}
	${({ borderRadius }) =>
        borderRadius !== undefined && `border-radius: ${borderRadius}${typeof borderRadius === "string" ? "" : "px"};`}
	${({ borderStyle }) => borderStyle && `border-style: ${borderStyle};`}
	${({ borderWidth }) =>
        borderWidth !== undefined && `border-width: ${borderWidth}${typeof borderWidth === "number" ? "px" : ""};`}
	${({ boxShadow }) => boxShadow && `box-shadow: ${boxShadow};`}
	${({ color, theme }) => color && `color: ${theme.v2.colors[color]};`}
	${({ flexWrap }) => flexWrap !== undefined && `flex-wrap: ${flexWrap};`}
	${({ fill, theme }) => fill && `fill: ${theme.v2.colors[fill]};`}
	${({ gap, spacing }) => {
        if (spacing !== undefined) {
            return `gap: ${spacingMixin(spacing)};`;
        }
        return gap !== undefined && `gap: ${gap}${typeof gap === "string" ? "" : "px"};`;
    }}
	${({ grow }) => grow !== undefined && `flex-grow: ${grow};`}
	${({ shrink }) => shrink !== undefined && `flex-shrink: ${shrink};`}
	${({ justifyContent }) => justifyContent && `justify-content: ${justifyContent};`}
    
    ${paddingMixin()};

    ${({ height }) => height && `height: ${height}${typeof height === "number" ? "px" : ""};`}
    ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight}${typeof maxHeight === "number" ? "px" : ""};`}
    ${({ minHeight }) => minHeight && `min-height: ${minHeight}${typeof minHeight === "number" ? "px" : ""};`}
    ${({ width }) => width && `width: ${width}${typeof width === "number" ? "px" : ""};`}
    ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}${typeof maxWidth === "number" ? "px" : ""};`}
    ${({ minWidth }) => minWidth && `min-width: ${minWidth}${typeof minWidth === "number" ? "px" : ""};`}
`;

Flex.displayName = "Flex";
